import { type FC } from 'react'
import { Button } from '../../../../components/Button/Button'
import { HostMode, type SchedulingEventType } from '../../../../models/Scheduling'
import { PlaceChip } from '../../../Scheduling/rows/PlaceChip'
import { HostStateLabel } from '../../../Scheduling/shared/HostStateLabel'
import { hostWarning } from '../../../Scheduling/shared/eventTypeHostState'
import { SettingsRow } from '../sections/SettingsSection'

interface AccountPageEventTypeRowProps {
  eventType: SchedulingEventType
  canEdit: boolean
  quiet: boolean
  onEditHosts: () => void
  onRemove: () => void
}

const hostLine = (eventType: SchedulingEventType): { icon: string; text: string } | null => {
  const summary = eventType.hostSummary
  if (!summary || summary.hostCount === 0) {
    return null
  }
  if (eventType.hostMode === HostMode.SINGLE) {
    return { icon: 'bi-person-fill', text: summary.singleHostName ? `One host · ${summary.singleHostName}` : 'One host' }
  }
  if (summary.hostCount === 1) {
    return { icon: 'bi-people-fill', text: `Rotating · ${summary.singleHostName ?? '1 host'}` }
  }
  return { icon: 'bi-people-fill', text: `Rotating · ${summary.hostCount} hosts` }
}

const hostStatus = (eventType: SchedulingEventType): { tone: 'warning' | 'muted'; text: string } | null => {
  const warning = hostWarning(eventType)
  const summary = eventType.hostSummary
  if (!warning || !summary) {
    return null
  }
  if (warning === 'needs-hosts') {
    return { tone: 'warning', text: 'No hosts assigned' }
  }
  if (warning === 'no-eligible-host') {
    return {
      tone: 'warning',
      text: eventType.hostMode === HostMode.SINGLE ? "The host can't take bookings." : 'No host can take bookings',
    }
  }
  const ineligibleCount = summary.hostCount - summary.eligibleHostCount
  return { tone: 'muted', text: `${ineligibleCount} host${ineligibleCount === 1 ? " can't" : "s can't"} take bookings` }
}

export const AccountPageEventTypeRow: FC<AccountPageEventTypeRowProps> = ({ eventType, canEdit, quiet, onEditHosts, onRemove }) => {
  const line = hostLine(eventType)
  const status = quiet ? null : hostStatus(eventType)
  const hasHosts = (eventType.hostSummary?.hostCount ?? 0) > 0

  return (
    <SettingsRow>
      <div role='group' aria-label={eventType.name} className='d-flex align-items-center gap-3'>
        <div className='d-flex flex-column flex-grow-1 zocks-gap-0.25'>
          <div className='d-flex align-items-center flex-wrap zocks-gap-0.5'>
            <span className='bold'>{eventType.name}</span>
            <span className='zocks-text-gray-600'>·</span>
            <span className='paragraph-small zocks-text-gray-600'>{eventType.durationMinutes} min</span>
            <PlaceChip meetingMode={eventType.meetingMode} />
          </div>
          {line ? (
            <span className='d-inline-flex align-items-center zocks-gap-0.25 paragraph-extra-small zocks-text-gray-600'>
              <i className={`bi ${line.icon}`} aria-hidden='true' />
              {line.text}
            </span>
          ) : null}
        </div>
        {status ? <HostStateLabel tone={status.tone}>{status.text}</HostStateLabel> : null}
        {canEdit ? (
          <>
            <Button variant='grayButton' onClick={onEditHosts}>
              {hasHosts ? 'Edit hosts' : 'Set up hosts'}
            </Button>
            <Button variant='grayButton' onClick={onRemove}>
              Remove
            </Button>
          </>
        ) : (
          <Button variant='grayButton' onClick={onEditHosts}>
            View
          </Button>
        )}
      </div>
    </SettingsRow>
  )
}
